import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, User as UserIcon, X } from "lucide-react";
import api, { API } from "../lib/api";
import { useI18n } from "../lib/i18n";
import AvatarCropModal from "./AvatarCropModal";

export default function AvatarUploader({ user, onUpdated, size = 96 }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const inputRef = useRef(null);

  const src = user?.avatar_id ? `${API}/files/${user.avatar_id}` : null;
  const initials = (user?.name || "?").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase();

  const pick = () => inputRef.current?.click();

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  const uploadBlob = async (blob) => {
    setCropSrc(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], "avatar.jpg", { type: "image/jpeg" }));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: updated } = await api.patch("/auth/me", { avatar_id: data.id });
      toast.success(t("photo_updated"));
      onUpdated?.(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(t("confirm_remove_photo"))) return;
    setBusy(true);
    try {
      const { data: updated } = await api.patch("/auth/me", { avatar_id: "" });
      toast.success(t("photo_removed"));
      onUpdated?.(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  return (
    <div className="relative inline-block" style={{ width: size, height: size }}>
      <div
        className="rounded-full overflow-hidden bg-[#143122] text-[#FAFAFA] flex items-center justify-center font-serif-display"
        style={{ width: size, height: size, fontSize: size * 0.34 }}
      >
        {src ? (
          <img data-testid="avatar-img" src={src} alt={user?.name || "avatar"} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <span>{initials || <UserIcon size={size * 0.4} strokeWidth={1.5} />}</span>
        )}
      </div>
      <button
        type="button"
        data-testid="avatar-upload-btn"
        onClick={pick}
        disabled={busy}
        className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-white border border-[#E6E2D8] shadow flex items-center justify-center text-[#143122] hover:bg-[#F4F1E9] transition-colors"
        title={src ? t("replace_photo") : t("upload_photo")}
        aria-label={src ? t("replace_photo") : t("upload_photo")}
      >
        <Camera size={16} strokeWidth={1.75} />
      </button>
      {src && (
        <button
          type="button"
          data-testid="avatar-remove-btn"
          onClick={remove}
          disabled={busy}
          className="absolute -bottom-1 -left-1 w-9 h-9 rounded-full bg-white border border-[#E6E2D8] shadow flex items-center justify-center text-[#1A1F1C]/70 hover:text-[#143122] hover:bg-[#F4F1E9] transition-colors"
          title={t("remove_photo")}
          aria-label={t("remove_photo")}
        >
          <X size={16} strokeWidth={1.75} />
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="avatar-file-input" />
      <AvatarCropModal
        open={!!cropSrc}
        imageSrc={cropSrc}
        onCancel={() => setCropSrc(null)}
        onConfirm={uploadBlob}
      />
    </div>
  );
}
